import React, { useRef, useEffect, RefObject, useCallback } from "react";
import { usePostsUserAndCoauthors } from "./usePostsUserAndCoauthors";
import { recalculateTruncation } from "../../lib/truncateUtils";
import classNames from "classnames";
import UsersNameDisplay from "../users/UsersNameDisplay";
import UsersNameWithModal from "../ultraFeed/UsersNameWithModal";
import UserNameDeleted from "../users/UserNameDeleted";
import LWTooltip from "../common/LWTooltip";
import { defineStyles } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';

const styles = defineStyles("TruncatedAuthorsList", (_: ThemeType) => ({
  root: {
    position: "relative",
    overflow: "hidden",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    fontSize: 13,
  },
  item: {},
  placeholder: {},
  tooltip: {
    display: "flex",
    flexDirection: "column",
  },
  more: {
    paddingLeft: 4,
  },
  scratch: {
    position: "absolute",
    opacity: 0,
    pointerEvents: "none",
  },
}), { stylePriority: -2 });

const reformatAuthorPlaceholder = (
  moreCount: number,
  totalItems: number,
  moreNode: Element,
  useMoreSuffix: boolean,
) => {


  let text: string;
  if (moreCount === totalItems) {
    text = `${moreCount} authors`;
  } else {
    if (useMoreSuffix) {
      text = `+ ${moreCount} more`;
    } else {
      text = `+${moreCount}`;
    }
  }
  moreNode.innerHTML = moreNode.innerHTML.replace(
    /(\+ \d+(?: more)?)|(\d+ authors)/,
    text,
  );
}

const TruncatedAuthorsList = ({post, expandContainer, className, useMoreSuffix = true, useUltraFeedModal = false, showSubscribedIcon = false}: {
  post: PostsList | SunshinePostsList | PostsBestOfList,
  expandContainer: RefObject<HTMLDivElement|null>,
  className?: string,
  useMoreSuffix?: boolean,
  useUltraFeedModal?: boolean,
  showSubscribedIcon?: boolean,
}) => {
  const classes = useStyles(styles);
  const {isAnon, authors, topCommentAuthor} = usePostsUserAndCoauthors(post);
  const UserNameComponent = useUltraFeedModal ? UsersNameWithModal : UsersNameDisplay;
  const ref = useRef<HTMLDivElement>(null);

  const reformatAuthorPlaceholderCurried = useCallback(
    (moreCount: number, totalItems: number, moreNode: Element) => reformatAuthorPlaceholder(moreCount, totalItems, moreNode, useMoreSuffix),
    [useMoreSuffix],
  );

  useEffect(() => {
    if (isAnon || authors.length === 0) {
      return;
    }

    const handler = () => recalculateTruncation(
      ref,
      expandContainer,
      classes,
      reformatAuthorPlaceholderCurried,
    );
    handler();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [isAnon, authors, topCommentAuthor, ref, classes, expandContainer, useMoreSuffix, reformatAuthorPlaceholderCurried]);
  return isAnon || authors.length === 0
    ? <UserNameDeleted />
    : (
      <div className={classNames(classes.root, className)} ref={ref}>
        <span className={classNames(classes.item, classes.placeholder)}>
          <UserNameComponent user={authors[0]} {...(useUltraFeedModal && { tooltipPlacement: "bottom-start", showSubscribedIcon })} />
        </span>
        <div className={classes.scratch} aria-hidden="true">
          {authors.map((author, i) =>
            <span key={author._id} className={classes.item}>
              {i > 0 ? ", " : ""}
              <UserNameComponent user={author} {...(useUltraFeedModal && { tooltipPlacement: "bottom-start", showSubscribedIcon })} />
            </span>
          )}
          {authors.length > 1 && (
            <LWTooltip
              title={
                <div className={classes.tooltip}>
                  {authors.slice(1).map((author: UsersMinimumInfo) => (
                    <UserNameComponent 
                      key={author._id} 
                      user={author} 
                      {...(useUltraFeedModal && { simple: true, showSubscribedIcon })}
                    />
                  ))}
                </div>
              }
              className={classes.more}
            >
              + 0 more
            </LWTooltip>
          )}
        </div>
      </div>
    );
}

export default TruncatedAuthorsList;
